/**
 * Operator DTOs exposed by operator-service `/api/operators` and
 * `/api/admin/operators`. Mirrors `OperatorView` + `Operator` entity.
 */

import { defensiveStatusMeta } from '../helpers/status-meta';
import type { UserRef } from './user.types';

export type OperatorStatus = 'PENDING' | 'ACTIVE' | 'EXHAUSTED' | 'SUSPENDED';
export type LicenseTier = 'BRONZE' | 'SILVER' | 'GOLD';
export type PrestataireType = 'OWNER' | 'DRIVER';

/** Row shape returned by `GET /api/operators` (paginated list). */
/**
 * Statut du dossier KYC d'un opérateur (#66) — prérequis à son recrutement de
 * prestataires. `PENDING` → `UNDER_REVIEW` (file admin) → `VALIDATED` | `REJECTED`.
 */
export type OperatorKycStatus = 'PENDING' | 'UNDER_REVIEW' | 'VALIDATED' | 'REJECTED';

export interface OperatorView {
  id: number;
  userId: number;
  businessName: string | null;
  zoneCode: string | null;
  licenseTier: LicenseTier | null;
  isFallback: boolean | null;
  isSystemOwned: boolean | null;
  operatorStatus: OperatorStatus;
  /** Dossier KYC (#66). L'opérateur ne peut recruter que si `VALIDATED`. */
  kycStatus?: OperatorKycStatus;
  /** Motif du rejet, renseigné uniquement quand `kycStatus === 'REJECTED'`. */
  kycRejectionReason?: string | null;
  customTotalPct?: number | null;
  customPlatformPct?: number | null;
  customOperatorPct?: number | null;
  creditReferenceAmount?: number | null;
  /** Read-model du gérant. Présent depuis le patch back qui enrichit `OperatorView`. */
  user?: UserRef | null;
}

/** Full Operator entity returned by `GET /api/operators/{id}` (richer fields). */
export interface Operator extends OperatorView {
  // BaseEntity fields
  createdAt?: string | null;
  updatedAt?: string | null;
  // Extra fields exposed via detail view
  creditThresholdState?: number | null;
  phone?: string | null;
  email?: string | null;
  /** Common BaseEntity zone — already in zoneCode but back also has zoneId. */
  zoneId?: number | null;
}

/** Body of `POST /api/operators/{id}/approve`. All fields optional. */
export interface ApproveOperatorRequest {
  licenseTier?: LicenseTier | string;
  customTotalPct?: number | null;
  customPlatformPct?: number | null;
  customOperatorPct?: number | null;
}

/**
 * Body of `POST /api/admin/operators` — création d'un opérateur (admin).
 * Deux modes pour la personne cible (flow « rechercher d'abord » §13.A) :
 *  - **user existant** : `userId` fourni (résolu via `/auth/api/users/resolve`).
 *  - **nouveau compte** : `userId` absent → `firstName`/`lastName` + `email`
 *    et/ou `phone` (au moins l'un des deux). Le back provisionne le compte et
 *    envoie une invitation set-password.
 */
export interface AdminCreateOperatorRequest {
  userId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  businessName: string;
  zoneCode: string;
  licenseTier?: LicenseTier | null;
  phone?: string | null;
  email?: string | null;
}

/** Body of `PATCH /api/admin/operators/{id}` — partial update of business fields.
 *  Only non-null fields are applied (back ignores `null`). */
export interface AdminUpdateOperatorRequest {
  businessName?: string | null;
  zoneCode?: string | null;
  licenseTier?: LicenseTier | null;
  phone?: string | null;
  email?: string | null;
}

/** Returned by `GET /api/operators/{id}/effective-rates`. Combines
 *  the operator's custom override (if any) with the global defaults
 *  from `CommissionConfig`. */
export interface EffectiveRatesResponse {
  operatorId: number;
  totalPct: number;
  platformPct: number;
  operatorPct: number;
  /** true if the override is applied, false if globals are in effect. */
  usesCustomRates: boolean;
}

/** Body of `PUT /api/admin/operators/{id}/credit-reference`. */
export interface CreditReferenceRequest {
  /** `null` removes the override and falls back to the global default. */
  amount: number | null;
  /** Audit reason, free-text. */
  reason: string;
}

// ── UI helpers ─────────────────────────────────────────────────────────

export const OPERATOR_STATUS_META = defensiveStatusMeta<OperatorStatus>({
  PENDING: { label: 'En attente', palette: 'warning' },
  ACTIVE: { label: 'Actif', palette: 'brand' },
  EXHAUSTED: { label: 'CREDIT épuisé', palette: 'error' },
  SUSPENDED: { label: 'Suspendu', palette: 'gray' },
});

export const LICENSE_TIER_LABEL: Record<LicenseTier, string> = {
  BRONZE: 'Bronze',
  SILVER: 'Argent',
  GOLD: 'Or',
};

export const LICENSE_TIER_OPTIONS: Array<{ value: LicenseTier; label: string }> = [
  { value: 'BRONZE', label: 'Bronze' },
  { value: 'SILVER', label: 'Argent' },
  { value: 'GOLD', label: 'Or' },
];
